import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Dashboard, DashboardSection } from "../../services/api/api";

export type DashboardTemplate = "empty" | "copy-current" | "default";

interface CreateDashboardDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description: string;
    template: DashboardTemplate;
  }) => Promise<void>;
  currentDashboard: Dashboard | null;
  defaultLayout: DashboardSection[];
  loading?: boolean;
  error?: string | null;
  /** Mode: 'create' for new dashboard, 'rename' for editing existing */
  mode?: "create" | "rename";
  /** Initial name when renaming */
  initialName?: string;
  /** Initial description when renaming */
  initialDescription?: string;
}

const CreateDashboardDialog: React.FC<CreateDashboardDialogProps> = ({
  open,
  onClose,
  onCreate,
  currentDashboard,
  defaultLayout,
  loading = false,
  error = null,
  mode = "create",
  initialName = "",
  initialDescription = "",
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [template, setTemplate] = useState<DashboardTemplate>("copy-current");

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setTemplate("copy-current");
    }
  }, [open, initialName, initialDescription]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onCreate({
      name: name.trim(),
      description: description.trim(),
      template,
    });
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setTemplate("copy-current");
    onClose();
  };

  const isRenameMode = mode === "rename";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isRenameMode ? "Rename Dashboard" : "Create New Dashboard"}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Dashboard Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Personal Finance, Business Overview"
            sx={{ mb: 2 }}
            required
            error={!name.trim() && name !== ""}
            helperText={!name.trim() && name !== "" ? "Name is required" : ""}
          />

          <TextField
            fullWidth
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A brief description of this dashboard"
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          {!isRenameMode && (
            <FormControl fullWidth>
              <InputLabel>Start From</InputLabel>
              <Select
                value={template}
                label="Start From"
                onChange={(e) =>
                  setTemplate(e.target.value as DashboardTemplate)
                }
              >
                <MenuItem value="copy-current">
                  Copy Current Dashboard
                  {currentDashboard && ` (${currentDashboard.name})`}
                </MenuItem>
                <MenuItem value="default">Default Layout</MenuItem>
                <MenuItem value="empty">Empty Dashboard</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !name.trim()}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : isRenameMode ? (
            "Save"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDashboardDialog;
